import exifr from 'exifr';

export interface GeocodeResult {
  placeName: string;
  city?: string;
  state?: string;
  country?: string;
  latitude: number;
  longitude: number;
  isFallback?: boolean;
}

export interface PhotoGpsItem {
  photoIndex: number;
  fileName: string;
  hasGps: boolean;
  latitude?: number;
  longitude?: number;
  placeName?: string;
  statusText: string;
}

export interface DeviceGpsResult {
  latitude: number;
  longitude: number;
  accuracyMeters: number;
  capturedAt: string; // ISO timestamp
  placeName: string;
  city?: string;
  country?: string;
}

/**
 * Performs reverse geocoding via authorized proxy or client-side fallback
 */
export async function reverseGeocode(latitude: number, longitude: number): Promise<GeocodeResult> {
  const latRef = latitude >= 0 ? 'N' : 'S';
  const lngRef = longitude >= 0 ? 'E' : 'W';
  const fallbackCoords = `${Math.abs(latitude).toFixed(4)}° ${latRef}, ${Math.abs(longitude).toFixed(4)}° ${lngRef}`;

  try {
    const res = await fetch(`/api/geocode/reverse?lat=${latitude}&lng=${longitude}`);
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.placeName) {
        return {
          placeName: data.placeName,
          city: data.city,
          state: data.state,
          country: data.country,
          latitude,
          longitude,
          isFallback: Boolean(data.isFallback),
        };
      }
    }
  } catch (err) {
    console.warn('Reverse geocoding network notice:', err);
  }

  return {
    placeName: fallbackCoords,
    latitude,
    longitude,
    isFallback: true,
  };
}

/**
 * Extracts GPS coordinates from a single photograph using EXIF metadata.
 * Converts EXIF tags to decimal degrees and reverse-geocodes to a readable place name.
 */
export async function extractGpsFromPhoto(
  file: File,
  photoIndex: number = 0
): Promise<PhotoGpsItem> {
  if (!file || !file.type.startsWith('image/')) {
    return {
      photoIndex,
      fileName: file?.name || 'Archivo',
      hasGps: false,
      statusText: 'Esta foto no contiene información de ubicación',
    };
  }

  try {
    const gps = await exifr.gps(file);
    if (
      gps &&
      typeof gps.latitude === 'number' &&
      typeof gps.longitude === 'number' &&
      !isNaN(gps.latitude) &&
      !isNaN(gps.longitude) &&
      (Math.abs(gps.latitude) > 0.0001 || Math.abs(gps.longitude) > 0.0001)
    ) {
      const { latitude, longitude } = gps;
      const geocoded = await reverseGeocode(latitude, longitude);

      return {
        photoIndex,
        fileName: file.name,
        hasGps: true,
        latitude,
        longitude,
        placeName: geocoded.placeName,
        statusText: `Ubicación detectada en la foto: ${geocoded.placeName}`,
      };
    }
  } catch (err) {
    console.warn('Notice reading EXIF GPS for', file.name, err);
  }

  return {
    photoIndex,
    fileName: file.name,
    hasGps: false,
    statusText: 'Esta foto no contiene información de ubicación',
  };
}

/**
 * Analyzes a batch of uploaded files and identifies GPS coordinates for each photo.
 */
export async function extractGpsFromPhotoBatch(files: File[]): Promise<PhotoGpsItem[]> {
  if (!files || files.length === 0) return [];

  const promises = files.map((file, idx) => extractGpsFromPhoto(file, idx));
  return Promise.all(promises);
}

export type GeolocationErrorCode =
  | 'PERMISSION_DENIED'
  | 'POSITION_UNAVAILABLE'
  | 'TIMEOUT'
  | 'NOT_SUPPORTED'
  | 'UNKNOWN';

export interface GeolocationErrorDetails {
  code: GeolocationErrorCode;
  message: string;
}

/**
 * Obtains real GPS location using the browser Geolocation API with high accuracy.
 */
export function getCurrentDeviceGps(): Promise<DeviceGpsResult> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !navigator.geolocation) {
      reject({
        code: 'NOT_SUPPORTED',
        message: 'Tu navegador o dispositivo no soporta geolocalización GPS.',
      } as GeolocationErrorDetails);
      return;
    }

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 12000,
      maximumAge: 0, // Fresh reading
    };

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const latitude = pos.coords.latitude;
        const longitude = pos.coords.longitude;
        const accuracyMeters = Math.round(pos.coords.accuracy || 0);
        const capturedAt = new Date(pos.timestamp || Date.now()).toISOString();

        try {
          const geocoded = await reverseGeocode(latitude, longitude);
          resolve({
            latitude,
            longitude,
            accuracyMeters,
            capturedAt,
            placeName: geocoded.placeName,
            city: geocoded.city,
            country: geocoded.country,
          });
        } catch {
          const latRef = latitude >= 0 ? 'N' : 'S';
          const lngRef = longitude >= 0 ? 'E' : 'W';
          const fallbackName = `${Math.abs(latitude).toFixed(4)}° ${latRef}, ${Math.abs(longitude).toFixed(4)}° ${lngRef}`;
          resolve({
            latitude,
            longitude,
            accuracyMeters,
            capturedAt,
            placeName: fallbackName,
          });
        }
      },
      (err) => {
        let code: GeolocationErrorCode = 'UNKNOWN';
        let message = 'No se pudo obtener la ubicación GPS.';

        switch (err.code) {
          case err.PERMISSION_DENIED:
            code = 'PERMISSION_DENIED';
            message = 'Permiso de ubicación denegado. Puedes publicar sin ubicación o activarla en los ajustes de tu navegador.';
            break;
          case err.POSITION_UNAVAILABLE:
            code = 'POSITION_UNAVAILABLE';
            message = 'Señal GPS no disponible en este momento. Revisa tu conexión o GPS.';
            break;
          case err.TIMEOUT:
            code = 'TIMEOUT';
            message = 'Tiempo de espera agotado al conectar con el GPS.';
            break;
        }

        reject({ code, message } as GeolocationErrorDetails);
      },
      options
    );
  });
}
